<script lang="ts">
  /**
   * TagPicker - chip-style multi-select tag input with type-to-filter
   * suggestions from the cached tag vocabulary (lib/stores/tags.ts).
   *
   * The vocabulary is fetched once and filtered client-side; the suggestion
   * list is ordered most-recently-used first. Typing a name that doesn't
   * exist offers "Create" — the server find-or-creates by case-insensitive
   * name on save (Taggable concern).
   *
   * State is owned by the parent: `selected` (name strings) in, `onChange`
   * (full replacement array) out.
   *
   * `variant="inline"` is the compact form for property rows (TaskDetailPopover):
   * smaller chips and a borderless "+ Add" field that only shows a box on focus.
   */
  import { tick } from 'svelte';
  import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
  import { tags as tagsStore, loadTags, invalidateTags, filterTags } from '../lib/stores/tags';

  interface Props {
    /** Currently selected tag names (parent-owned state) */
    selected: string[];
    /** Called with the full replacement list on any add/remove */
    onChange: (names: string[]) => void;
    placeholder?: string;
    variant?: 'default' | 'inline';
  }

  let { selected, onChange, placeholder, variant = 'default' }: Props = $props();

  let inline = $derived(variant === 'inline');
  let resolvedPlaceholder = $derived(placeholder ?? (inline ? '+ Add' : 'Add tag...'));

  let open = $state(false);
  let query = $state('');
  let selectedIndex = $state(0);
  let inputEl: HTMLInputElement | undefined = $state();
  let containerEl: HTMLDivElement | undefined = $state();
  let listEl: HTMLDivElement | undefined = $state();

  const MAX_SUGGESTIONS = 8;

  let suggestions = $derived(filterTags($tagsStore, query, selected).slice(0, MAX_SUGGESTIONS));
  let exactMatch = $derived(
    suggestions.some((t) => t.name.toLowerCase() === query.trim().toLowerCase()),
  );
  let canCreate = $derived(query.trim().length > 0 && !exactMatch && !alreadySelected(query));
  let showList = $derived(open && (suggestions.length > 0 || canCreate));

  function alreadySelected(name: string): boolean {
    const q = name.trim().toLowerCase();
    return selected.some((s) => s.toLowerCase() === q);
  }

  function handleInputFocus() {
    open = true;
    query = '';
    selectedIndex = 0;
    loadTags();
  }

  function addTag(name: string) {
    const trimmed = name.trim();
    if (!trimmed || alreadySelected(trimmed)) return;
    onChange([...selected, trimmed]);
    invalidateTags();
    query = '';
    selectedIndex = 0;
    tick().then(() => inputEl?.focus());
  }

  function removeTag(name: string) {
    onChange(selected.filter((n) => n !== name));
    invalidateTags();
  }

  function handleInputKeydown(e: KeyboardEvent) {
    if (!showList) {
      if (e.key === 'Enter' && query.trim()) {
        e.preventDefault();
        addTag(query);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const max = suggestions.length + (canCreate ? 1 : 0);
      selectedIndex = (selectedIndex + 1) % max;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const max = suggestions.length + (canCreate ? 1 : 0);
      selectedIndex = (selectedIndex - 1 + max) % max;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (canCreate && selectedIndex === suggestions.length) {
        addTag(query);
      } else if (suggestions[selectedIndex]) {
        addTag(suggestions[selectedIndex].name);
      } else if (query.trim()) {
        addTag(query);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      open = false;
    }
  }

  // eslint-disable-next-line momentium/no-unguarded-effect -- close on outside pointer-down; reads containerEl ref
  $effect(() => {
    if (!open || !containerEl) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!containerEl?.contains(e.target as Node)) open = false;
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- position the dropdown with Floating UI when it opens
  $effect(() => {
    if (!showList || !containerEl || !listEl) return;
    const update = () => {
      if (!containerEl || !listEl) return;
      computePosition(containerEl, listEl, {
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [offset(4), flip(), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        if (!listEl) return;
        Object.assign(listEl.style, {
          left: `${x}px`,
          top: `${y}px`,
          position: 'fixed',
        });
      });
    };
    update();
    const cleanup = [autoUpdate(containerEl, listEl, update)];
    return () => cleanup.forEach((fn) => fn());
  });
</script>

<div class="flex flex-wrap items-center {inline ? 'gap-1' : 'gap-1.5'}" bind:this={containerEl}>
  {#each selected as name (name)}
    <span
      class="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 font-medium text-blue-700 {inline
        ? 'px-1.5 py-px text-[11px] leading-4'
        : 'px-2 py-0.5 text-xs'}"
    >
      {name}
      <button
        type="button"
        class="ml-0.5 rounded-full text-blue-400 transition-colors hover:text-blue-700"
        aria-label={`Remove tag ${name}`}
        onclick={() => removeTag(name)}
      >
        ×
      </button>
    </span>
  {/each}

  <input
    type="text"
    bind:this={inputEl}
    bind:value={query}
    autocomplete="off"
    onfocus={handleInputFocus}
    oninput={() => (selectedIndex = 0)}
    onkeydown={handleInputKeydown}
    placeholder={resolvedPlaceholder}
    aria-label="Add tags"
    class={inline
      ? 'h-5 w-14 min-w-[3.5rem] flex-1 rounded border border-transparent bg-transparent px-1 text-[11px] text-gray-700 transition-colors outline-none placeholder:text-gray-400 hover:bg-gray-50 focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-500/20'
      : 'min-w-[8rem] flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm transition-colors outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500'}
  />
  {#if showList}
    <div
      bind:this={listEl}
      class="z-[400] mt-1 max-h-48 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
      style="position: fixed; left: 0; top: 0;"
    >
      {#each suggestions as tag, i (tag.id)}
        <button
          type="button"
          class="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm transition-colors {selectedIndex ===
          i
            ? 'bg-blue-50 text-blue-700'
            : 'text-gray-700 hover:bg-gray-50'}"
          onpointerdown={(e) => e.preventDefault()}
          onclick={() => addTag(tag.name)}
        >
          <span>{tag.name}</span>
          <span class="text-xs text-gray-400">{tag.count}</span>
        </button>
      {/each}
      {#if canCreate}
        <button
          type="button"
          class="flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-sm transition-colors {selectedIndex ===
          suggestions.length
            ? 'bg-blue-50 text-blue-700'
            : 'text-gray-700 hover:bg-gray-50'}"
          onpointerdown={(e) => e.preventDefault()}
          onclick={() => addTag(query)}
        >
          <span class="font-medium">＋</span>
          <span>Create “{query.trim()}”</span>
        </button>
      {/if}
    </div>
  {/if}
</div>
